import type {
  FleetOperationalStatus,
  FleetUsageMode,
  FleetVehicleListItem,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  FLEET_CATEGORY_LABELS,
  FLEET_STATUS_LABELS,
  FLEET_USAGE_LABELS,
  formatFleetDueDate,
  isFleetDateOverdue,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { FleetStatusBadge } from '@/products/fleet/features/fleet-status-badge';
import { FleetVehicleDeleteButton } from '@/products/fleet/features/fleet-vehicle-delete-button';
import { useFleetVehicles } from '@/products/fleet/features/use-fleet';
import { fleetVehiclePath, FLEET_VEHICLE_NEW_PATH } from '@/products/fleet/fleet-paths';

function huLabel(vehicle: FleetVehicleListItem): string {
  if (vehicle.hu.missingRegistration) {
    return 'Erstzulassung fehlt';
  }
  return vehicle.hu.dueOn ? formatFleetDueDate(vehicle.hu.dueOn) : '—';
}

function huClassName(vehicle: FleetVehicleListItem): string | undefined {
  if (vehicle.hu.missingRegistration) {
    return 'font-medium text-amber-800 dark:text-amber-300';
  }
  if (isFleetDateOverdue(vehicle.hu.dueOn)) {
    return 'font-medium text-destructive';
  }
  return undefined;
}

const STATUS_FILTER: FleetOperationalStatus[] = [
  'in_service',
  'out_of_service',
  'in_workshop',
  'retired',
];

export function FleetVehiclesPage() {
  usePageTitle('Fahrzeuge');
  const [searchParams] = useSearchParams();
  const [includeRetired, setIncludeRetired] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [operationalStatus, setOperationalStatus] = useState<FleetOperationalStatus | ''>(
    () => (searchParams.get('operationalStatus') as FleetOperationalStatus | null) ?? '',
  );
  const [usageMode, setUsageMode] = useState<FleetUsageMode | ''>('');
  const [retiredOnPage, setRetiredOnPage] = useState(includeRetired);
  if (retiredOnPage !== includeRetired) {
    setRetiredOnPage(includeRetired);
    setPage(1);
  }
  const currentPage = retiredOnPage === includeRetired ? page : 1;
  const vehicles = useFleetVehicles(includeRetired, {
    page: currentPage,
    q: qApplied || undefined,
    operationalStatus: operationalStatus || undefined,
    usageMode: usageMode || undefined,
  });

  const items = vehicles.data?.items ?? [];
  const filtered = Boolean(qApplied || operationalStatus || usageMode);

  return (
    <div className="space-y-4">
      <PlatformPageHeader
        title="Fahrzeuge"
        action={
          <Button asChild>
            <Link to={FLEET_VEHICLE_NEW_PATH}>Fahrzeug anlegen</Link>
          </Button>
        }
      />
      <div
        className={`${fleetPanelClassName} flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end`}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor="vehicle-search">Suche</Label>
          <Input
            id="vehicle-search"
            value={q}
            placeholder="Kennzeichen, Hersteller, Modell"
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setQApplied(q);
                setPage(1);
              }
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="vehicle-status">Status</Label>
          <Select
            value={operationalStatus || 'none'}
            onValueChange={(value) => {
              setOperationalStatus(value === 'none' ? '' : (value as FleetOperationalStatus));
              setPage(1);
            }}
          >
            <SelectTrigger id="vehicle-status" className="w-full min-w-[10rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Alle</SelectItem>
              {STATUS_FILTER.map((status) => (
                <SelectItem key={status} value={status}>
                  {FLEET_STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="vehicle-usage">Nutzung</Label>
          <Select
            value={usageMode || 'none'}
            onValueChange={(value) => {
              setUsageMode(value === 'none' ? '' : (value as FleetUsageMode));
              setPage(1);
            }}
          >
            <SelectTrigger id="vehicle-usage" className="w-full min-w-[10rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Alle</SelectItem>
              {(Object.keys(FLEET_USAGE_LABELS) as FleetUsageMode[]).map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {FLEET_USAGE_LABELS[mode]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setQApplied(q);
            setPage(1);
          }}
        >
          Suchen
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={includeRetired}
          onChange={(event) => setIncludeRetired(event.target.checked)}
        />
        Stillgelegte zeigen
      </label>
      {vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrzeuge werden geladen …</p>
      ) : null}
      {vehicles.isError ? (
        <p className="text-sm text-destructive">
          {vehicles.error instanceof ApiRequestError
            ? vehicles.error.message
            : 'Die Fahrzeuge konnten nicht geladen werden.'}
        </p>
      ) : null}
      {vehicles.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {filtered ? 'Keine Fahrzeuge für diese Suche.' : 'Noch kein Fahrzeug angelegt.'}
        </p>
      ) : null}
      {items.length > 0 ? (
        <>
          <div className={`hidden md:block ${fleetTableFrameClassName}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kennzeichen</TableHead>
                  <TableHead>Fahrzeug</TableHead>
                  <TableHead>Fahrer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Kilometer</TableHead>
                  <TableHead>HU</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((vehicle) => (
                  <TableRow key={vehicle.id} className="hover:bg-muted/40">
                    <TableCell>
                      <Link
                        to={fleetVehiclePath(vehicle.id)}
                        className="font-semibold text-primary hover:underline"
                      >
                        {vehicle.licensePlate}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {vehicle.make} {vehicle.model} · {FLEET_CATEGORY_LABELS[vehicle.category]}
                    </TableCell>
                    <TableCell>{vehicle.holderName ?? '—'}</TableCell>
                    <TableCell>
                      <FleetStatusBadge status={vehicle.operationalStatus} />
                    </TableCell>
                    <TableCell>
                      {vehicle.currentKm != null
                        ? `${vehicle.currentKm.toLocaleString('de-DE')} km`
                        : '—'}
                    </TableCell>
                    <TableCell className={huClassName(vehicle)}>{huLabel(vehicle)}</TableCell>
                    <TableCell className="text-right">
                      <FleetVehicleDeleteButton vehicle={vehicle} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
            {items.map((vehicle) => (
              <li key={vehicle.id} className="space-y-2 px-4 py-3">
                <Link
                  to={fleetVehiclePath(vehicle.id)}
                  className="flex flex-col gap-2 hover:bg-muted/40"
                >
                  <span className="text-xl font-semibold tracking-tight text-primary">
                    {vehicle.licensePlate}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {vehicle.make} {vehicle.model} · {FLEET_CATEGORY_LABELS[vehicle.category]}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {vehicle.holderName ? `Fahrer: ${vehicle.holderName}` : null}
                    {vehicle.currentKm != null
                      ? ` · ${vehicle.currentKm.toLocaleString('de-DE')} km`
                      : null}
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    <FleetStatusBadge status={vehicle.operationalStatus} />
                    <span className={`text-sm text-muted-foreground ${huClassName(vehicle) ?? ''}`}>
                      HU {huLabel(vehicle)}
                    </span>
                  </span>
                </Link>
                <FleetVehicleDeleteButton vehicle={vehicle} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {vehicles.data ? (
        <ListPagination
          page={vehicles.data.page}
          pageSize={vehicles.data.pageSize}
          total={vehicles.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}
